import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  Tooltip,
  Stack,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Grid
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Edit as EditIcon } from "@mui/icons-material";
import TablePagination from "@mui/material/TablePagination";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import baseURL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from 'xlsx';


const FormIModal = ({ contractDetails }) => {
  const [activeRowIndex, setActiveRowIndex] = useState(-1);
  const [cocDates, setCocDates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingIndex, setLoadingIndex] = useState(-1);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // const userId = localStorage.getItem("user id");
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const userID = localStorage.getItem("user id");

  const handleStatusUpdate = async (contract, index) => {
    const cocDate = cocDates[index];
    if (!cocDate) {
      showToast("Please enter a date.", "error");
      return;
    }

    setLoadingIndex(index); // start loader

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${baseURL}/api/statusAfterForm1`,
        {
          contractId: contract.contractDetails?._id,
          coc: [{ Date: cocDate }],
          creatorId: userID,
          crewId: contract.contractDetails?.crewdetails?._id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        }
      );

      showToast(response.data.message, "success");
      setActiveRowIndex(-1);
      setCocDates({ ...cocDates, [index]: "" });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error updating status:", error);
      showToast(error.response?.data?.message || "Failed to update status", "error");
    } finally {
      setLoadingIndex(-1); // stop loader
    }
  };

  const showToast = (message, severity) => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredContracts = contractDetails.filter(contract =>
    contract.contractDetails?.ShipDetails?.vesselname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contract.contractDetails?.crewdetails?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contract.contractDetails?.crewingAgent?.agentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contract.contractDetails?.crewdetails?.indosno?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // console.log("filteredContracts", filteredContracts)

  const filteredCocData = filteredContracts.filter((row) => {
    const cocDateRaw = row.contractStatus?.formI?.uploadedAt;
    if (!cocDateRaw) return false;

    const cocDate = new Date(cocDateRaw);
    const month = String(cocDate.getMonth() + 1).padStart(2, '0');
    const year = String(cocDate.getFullYear());

    return (
      (!selectedMonth || month === selectedMonth) &&
      (!selectedYear || year === String(selectedYear))
    );
  });

  const handleExportToExcel = () => {
    if (!filteredCocData || filteredCocData.length === 0) {
      showToast("No data available to export", "info");
      return;
    }

    const exportData = filteredCocData.map((contract, i) => ({
      "Sr. No.": i + 1,
      "Vessel Name": contract.contractDetails?.ShipDetails?.vesselname || "N/A",
      "Company Name": contract.contractDetails?.vesselOwner?.company_shortname || "N/A",
      "Crew Name": contract.contractDetails?.crewdetails?.name || "N/A",
      "Indos No.": contract.contractDetails?.crewdetails?.indosno || "N/A",
      "Agent Name": contract.contractDetails?.crewingAgent?.agentName || "N/A",
      "Form I Date": contract.contractStatus?.formI?.uploadedAt
        ? new Date(contract.contractStatus.formI.uploadedAt).toLocaleDateString()
        : "N/A"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Form I Records");

    XLSX.writeFile(workbook, "FormI_Records.xlsx");
  };


  return (
    <Card elevation={3} sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
      <CardContent sx={{ p: 4 }}>
        <Box
          sx={{
            p: 1,
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap", // optional: remove if you want strict one-line layout
            gap: 2,
          }}
        >
          {/* Search */}
          <TextField
            placeholder="Search by vessel, crew, or agent name..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: "#f5f5f5",
              borderRadius: 2,
              width: 280,
            }}
          />

          {/* Title */}
          <Typography variant="h6" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
            Form I Status Management
          </Typography>

          {/* Filters */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* Filter 1: Month */}
            <FormControl size="small" sx={{ bgcolor: "white", borderRadius: 1, minWidth: 200 }}>
              <InputLabel>Month</InputLabel>
              <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} label="Month">
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Filter 2: Year */}
            <FormControl size="small" sx={{ bgcolor: "white", borderRadius: 1, minWidth: 200 }}>
              <InputLabel>Year</InputLabel>
              <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year">
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Export Button */}
            <Button
              variant="contained"
              size="small"
              onClick={handleExportToExcel}
              sx={{
                height: 40,
                backgroundColor: '#2e7d32', // light red
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1b5e20', // slightly darker red on hover
                },
              }}
            >
              Export to Excel
            </Button>

          </Box>


          {/* Count */}
          <Typography variant="h6" fontWeight="500" sx={{ whiteSpace: "nowrap" }}>
            Total Count: {filteredCocData.length}
          </Typography>
        </Box>


        {filteredCocData.length > 0 ? (
          <>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 500,
                overflowY: "auto",
                overflowX: "auto",
                borderRadius: 2,
                boxShadow: "none",
                border: "1px solid #e0e0e0",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Sr. No.</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Contract Details</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Form I</TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold", width: activeRowIndex !== -1 ? "340px" : "200px" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCocData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((contract, index) => (
                      <TableRow hover key={index}>
                        {/* {console.log("formI", contract)} */}
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              <strong style={{ color: "black" }}>
                                Vessel Name: {contract.contractDetails?.ShipDetails?.vesselname || "N/A"}
                              </strong>
                            </Typography>
                            <Typography variant="subtitle2" fontWeight="bold">
                              <strong style={{ color: "#2e7d32" }}>
                                Company Name: {contract.contractDetails?.vesselOwner?.company_shortname || "N/A"}
                              </strong>
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                              size="small"
                              label={`Crew: ${contract.contractDetails?.crewdetails?.name || "N/A"}`}
                              sx={{ bgcolor: "#e3f2fd", color: "#0d47a1" }}
                            />
                            <Chip
                              size="small"
                              label={`Indos No.: ${contract.contractDetails?.crewdetails?.indosno || "N/A"}`}
                              sx={{ bgcolor: "#fff9c4", color: "#f57f17" }}
                            />
                            <Chip
                              size="small"
                              label={`Agent: ${contract.contractDetails?.crewingAgent?.agentName || "N/A"}`}
                              sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a" }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell>
                          {contract.contractStatus?.formI?.filePath ? (
                            <IconButton
                              onClick={() => window.open(`${baseURL}/${contract.contractStatus.formI.filePath.replace(/\\/g, '/')}`, "_blank")}
                            >
                              <PictureAsPdfIcon sx={{ color: "red" }} />
                            </IconButton>
                          ) : (
                            <Chip
                              label="No File Available"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          {contract.contractStatus?.latestStatus?.adminStatus === null ? (
                            <Typography color="error" fontSize="0.875rem">
                              Contract requires 'Final Approval' before proceeding
                            </Typography>
                          ) : activeRowIndex === index ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                type="date"
                                label="COC Date"
                                InputLabelProps={{ shrink: true }}
                                value={cocDates[index] || ""}
                                onChange={(e) => setCocDates({ ...cocDates, [index]: e.target.value })}
                                size="small"
                                sx={{ width: "160px" }}
                                inputProps={{
                                  max: new Date().toISOString().split("T")[0],
                                }}
                              />
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleStatusUpdate(contract, index)}
                                sx={{ borderRadius: 2 }}
                                disabled={loadingIndex === index}
                              >
                                {loadingIndex === index ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  "Submit"
                                )}
                              </Button>
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => setActiveRowIndex(-1)}
                                sx={{ ml: 1 }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Tooltip title="Add COC Date">
                              <IconButton
                                color="primary"
                                onClick={() => setActiveRowIndex(index)}
                                size="small"
                                sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredContracts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                px: 2,
                bgcolor: "#fafafa",
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "flex-end"
              }}
            />
          </>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No contract details found matching your search criteria.
          </Alert>
        )}
      </CardContent>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
      <ToastContainer />
    </Card>
  );
};

export default FormIModal;
